import { useState, useEffect } from 'react';
import { db } from '../../firebase'; 
import { collection, query, where, getCountFromServer, orderBy, limit, onSnapshot } from 'firebase/firestore'; // getDocs 제거
import { Link } from 'react-router-dom'; 
import { ImageIcon, MessageSquareIcon, Edit3Icon, StarIcon } from '../../components/Icons'; // StarIcon 추가

const AdminDashboardPage = () => {
  const [totalImageCount, setTotalImageCount] = useState(0);
  const [newInquiryCount, setNewInquiryCount] = useState(0);
  const [recentActivities, setRecentActivities] = useState([]); 
  const [loadingStats, setLoadingStats] = useState(true);
  const [loadingActivities, setLoadingActivities] = useState(true); 

  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const imagesColl = collection(db, "constructionImages");
        const imageSnapshot = await getCountFromServer(imagesColl);
        setTotalImageCount(imageSnapshot.data().count);

        const inquiriesColl = collection(db, "inquiries");
        const newInquiriesQuery = query(inquiriesColl, where("status", "==", "신규"));
        const newInquiriesSnapshot = await getCountFromServer(newInquiriesQuery);
        setNewInquiryCount(newInquiriesSnapshot.data().count);

      } catch (error) {
        console.error("Error fetching count stats: ", error);
      }
    };

    const activitiesColl = collection(db, "activities");
    const recentActivitiesQuery = query(activitiesColl, orderBy("timestamp", "desc"), limit(5));
    
    const unsubscribeActivities = onSnapshot(recentActivitiesQuery, (querySnapshot) => {
      const fetchedActivities = [];
      querySnapshot.forEach(doc => {
        fetchedActivities.push({ id: doc.id, ...doc.data() });
      });
      setRecentActivities(fetchedActivities);
      setLoadingActivities(false); 
    }, (error) => {
      console.error("Error fetching recent activities (onSnapshot): ", error); 
      setRecentActivities([]); 
      setLoadingActivities(false);
    });

    const initialLoad = async () => {
        setLoadingStats(true);
        await fetchCounts();
        setLoadingStats(false);
    }

    initialLoad();

    return () => {
      unsubscribeActivities();
    };
  }, []); 

  const renderActivityIcon = (type) => {
    switch (type) {
      case 'new_inquiry':
        return <MessageSquareIcon className="w-4 h-4 text-amber-500 mr-3 flex-shrink-0" />;
      case 'image_upload':
        return <ImageIcon className="w-4 h-4 text-sky-500 mr-3 flex-shrink-0" />;
      case 'inquiry_status_change':
        return <Edit3Icon className="w-4 h-4 text-purple-500 mr-3 flex-shrink-0" />;
      // 대표 프로젝트 지정/해제 활동에 대한 아이콘 추가
      case 'image_feature_toggle':
        return <StarIcon className="w-4 h-4 text-yellow-400 mr-3 flex-shrink-0" />;
      case 'image_delete':
        return <ImageIcon className="w-4 h-4 text-red-500 mr-3 flex-shrink-0" />;
      case 'inquiry_delete':
        return <MessageSquareIcon className="w-4 h-4 text-red-500 mr-3 flex-shrink-0" />;
      default:
        return <div className="w-4 h-4 mr-3 flex-shrink-0 bg-gray-300 rounded-sm"></div>; 
    }
  };

  const isLoading = loadingStats || loadingActivities;

  return (
    <>
      <title>대시보드 - 관리자</title>
      <div>
        <h1 className="text-3xl font-bold text-gray-800 dark:text-white mb-6">관리자 대시보드</h1>
        
        {isLoading && totalImageCount === 0 && newInquiryCount === 0 && recentActivities.length === 0 ? ( 
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-pulse">
            {[1,2,3].map(i => (
              <div key={i} className="bg-white dark:bg-slate-800 p-6 rounded-lg shadow-md">
                <div className="h-6 bg-slate-200 dark:bg-slate-700 rounded w-3/4 mb-2"></div>
                <div className="h-8 bg-slate-300 dark:bg-slate-600 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Link to="/jy-dev/images" className="block bg-white dark:bg-slate-800 p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow">
              <h2 className="text-xl font-semibold text-gray-700 dark:text-gray-200">총 이미지 수</h2>
              <p className="text-4xl font-bold text-sky-600 dark:text-sky-400 mt-2">{loadingStats ? '...' : `${totalImageCount}개`}</p>
            </Link>
            <Link to="/jy-dev/inquiries" className="block bg-white dark:bg-slate-800 p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow">
              <h2 className="text-xl font-semibold text-gray-700 dark:text-gray-200">새로운 문의</h2>
              <p className="text-4xl font-bold text-amber-500 mt-2">{loadingStats ? '...' : `${newInquiryCount}건`}</p>
              {newInquiryCount > 0 && !loadingStats && <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">확인이 필요합니다.</p>}
            </Link>
            <div className="md:col-span-2 lg:col-span-1 bg-white dark:bg-slate-800 p-6 rounded-lg shadow-md">
              <h2 className="text-xl font-semibold text-gray-700 dark:text-gray-200 mb-4">최근 활동</h2>
              {loadingActivities ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">활동 내역을 불러오는 중...</p>
              ) : recentActivities.length > 0 ? (
                <ul className="space-y-3 max-h-60 overflow-y-auto"> 
                  {recentActivities.map(activity => (
                    <li key={activity.id} className="flex items-start text-sm border-b border-slate-100 dark:border-slate-700 pb-2 last:border-b-0 last:pb-0">
                      {renderActivityIcon(activity.type)}
                      <div className="flex-grow">
                        <span className="text-gray-700 dark:text-gray-300">{activity.text}</span>
                        <span className="block text-xs text-gray-400 dark:text-gray-500">
                          {activity.timestamp?.toDate ? activity.timestamp.toDate().toLocaleString() : '시간 정보 없음'}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400">최근 활동이 없습니다.</p>
              )}
            </div>
          </div>
        )}

        <div className="mt-12 bg-white dark:bg-slate-800 p-6 rounded-lg shadow-md">
          <h2 className="text-2xl font-semibold text-gray-700 dark:text-gray-200 mb-4">빠른 작업</h2>
          <div className="flex flex-wrap gap-4">
            <Link to="/jy-dev/images" className="bg-sky-500 hover:bg-sky-600 text-white font-medium py-2 px-4 rounded-lg transition-colors">
              이미지 관리 바로가기
            </Link>
            <Link to="/jy-dev/inquiries" className="bg-amber-500 hover:bg-amber-600 text-white font-medium py-2 px-4 rounded-lg transition-colors">
              문의 확인 바로가기
            </Link>
          </div>
        </div>
        
      </div>
    </>
  );
};
export default AdminDashboardPage;
